import { useCallback, useEffect, useRef, useState } from 'react';
import { SearchIcon } from 'assets/images/countryList';
import { searchCurrencies } from 'services/organization/currency.service';
import { ICurrencySearchItem } from 'types/currency.types';
import { ISelectedCountryCurrency } from 'types/countries';
import CurrencyNameCell from '../../../currenciesTab/components/currencyNameCell/CurrencyNameCell';
import AddCurrencyModalForm, { ICreatedCurrency } from '../../../addCurrencyModal/AddCurrencyModalForm';
import { bordColorTheme, nudosGray300, nudosTextcolor } from 'styles/styles.constants';
import './newCountryCurrencySelect.scss';

interface INewCountryCurrencySelect {
  selectedCurrencyCode?: string;
  onCurrencyChange: (currency: ISelectedCountryCurrency) => void;
}

/**
 * Selector de moneda para el formulario de nuevo país con búsqueda y opción de crear moneda.
 */
const NewCountryCurrencySelect = ({ selectedCurrencyCode, onCurrencyChange }: INewCountryCurrencySelect) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [currencyOptions, setCurrencyOptions] = useState<ICurrencySearchItem[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalFormKey, setModalFormKey] = useState(0);
  const [initialCurrencyName, setInitialCurrencyName] = useState('');

  const hasSelectedCurrency = !!selectedCurrencyCode;
  const normalizedSearchQuery = searchQuery.trim().toLowerCase();

  const loadCurrencyOptions = useCallback(async () => {
    setIsLoadingOptions(true);

    try {
      const options = await searchCurrencies();
      setCurrencyOptions(options ?? []);
    } catch {
      setCurrencyOptions([]);
    } finally {
      setIsLoadingOptions(false);
    }
  }, []);

  const closeDropdown = () => {
    setIsDropdownOpen(false);
    setSearchQuery('');
  };

  const handleOpenCreateModal = () => {
    setInitialCurrencyName(searchQuery.trim());
    setModalFormKey(previousKey => previousKey + 1);
    setIsModalOpen(true);
    closeDropdown();
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleCurrencyCreated = async (createdCurrency?: ICreatedCurrency) => {
    await loadCurrencyOptions();

    if (createdCurrency?.code) {
      onCurrencyChange({
        code: createdCurrency.code,
        exchangeRate: createdCurrency.exchangeRate
      });
    }
  };

  const handleSelectCurrency = (currency: ICurrencySearchItem) => {
    onCurrencyChange({
      code: currency.code,
      exchangeRate: currency.exchangeRate ?? null
    });
    closeDropdown();
  };

  const handleSearchFocus = () => {
    setIsDropdownOpen(true);
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setIsDropdownOpen(true);
  };

  const filteredOptions = currencyOptions.filter(option => {
    if (!normalizedSearchQuery) return true;

    return (
      option.code?.toLowerCase().includes(normalizedSearchQuery) ||
      option.name?.toLowerCase().includes(normalizedSearchQuery)
    );
  });

  const shouldShowCreateOption = !!normalizedSearchQuery && !isLoadingOptions && filteredOptions.length === 0;

  const formatSearchDisplay = (value: string) => {
    return value.length > 15 ? `${value.substring(0, 15)}...` : value;
  };

  const inputValue = isDropdownOpen ? searchQuery : selectedCurrencyCode ?? '';

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
      closeDropdown();
    }
  }, []);

  useEffect(() => {
    loadCurrencyOptions();
  }, [loadCurrencyOptions]);

  useEffect(() => {
    if (!isDropdownOpen) return;

    window.addEventListener('mousedown', handleClickOutside);
    return () => window.removeEventListener('mousedown', handleClickOutside);
  }, [isDropdownOpen, handleClickOutside]);

  const renderDropdownContent = () => {
    if (isLoadingOptions) {
      return <div className="dropdownEmptyState">Cargando...</div>;
    }

    if (!filteredOptions.length && !shouldShowCreateOption) {
      return <div className="dropdownEmptyState">Sin resultados</div>;
    }

    return (
      <>
        {filteredOptions.map(currency => (
          <button
            key={currency.id}
            type="button"
            className="dropdownOption"
            onClick={() => handleSelectCurrency(currency)}
          >
            <CurrencyNameCell flag={currency.flag} name={currency.name} />
          </button>
        ))}

        {shouldShowCreateOption && (
          <button type="button" className="newCurrencyOption" onClick={handleOpenCreateModal}>
            {`${formatSearchDisplay(searchQuery.trim())} - `}
            <span className="createCurrencyAction">Nuevo</span>
          </button>
        )}
      </>
    );
  };

  return (
    <>
      <div className="newCountryCurrencySelect" ref={containerRef}>
        <div
          className={`currencySearchBox customBorder ${hasSelectedCurrency ? 'filled' : ''} ${
            isDropdownOpen ? 'open' : ''
          }`}
        >
          <div className="searchIconWrapper">
            <SearchIcon
              stroke={hasSelectedCurrency || isDropdownOpen ? nudosTextcolor : nudosGray300}
              fill={bordColorTheme.white.default}
            />
          </div>
          <input
            className="currencySearchInput"
            type="text"
            placeholder="Buscar moneda"
            value={inputValue}
            onFocus={handleSearchFocus}
            onChange={event => handleSearchChange(event.target.value)}
          />
        </div>

        {isDropdownOpen && (
          <div className="currencyDropdownPanel">
            <div className="currencyOptionsList">{renderDropdownContent()}</div>
          </div>
        )}
      </div>

      {isModalOpen && (
        <AddCurrencyModalForm
          key={modalFormKey}
          initialCurrencyName={initialCurrencyName}
          onClose={handleCloseModal}
          onCurrencyCreated={handleCurrencyCreated}
        />
      )}
    </>
  );
};

export default NewCountryCurrencySelect;
